import { IonButton, IonList } from '@ionic/react';
import { Form, Formik, FormikProps } from 'formik';
import { isEmpty, isNil, merge } from 'lodash';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';
import {
  ListMachinesDocument,
  MachineConnectionType,
  ProvisionMachineInput,
  ProvisionedMachine,
  useListScratchpadAppsLazyQuery,
  useProvisionMachineMutation,
} from '../../../api/graphql/generated';
import {
  trackReactivatingMachine,
  trackRegisterMachine,
} from '../../../config/analytics';
import { ApplicationConfig, isLocalDev } from '../../../config/config';
import i18n from '../../../i18n';
import { logger } from '../../../util/logger';
import { IonFormField } from '../../form/ion-form.field';
import { QrCodeScannerButton } from '../../qr-code/qr-code-scanner-button';
import { SaveStatusToast } from '../../toast/toast.gql';
import { RegisterMachineProps } from './register-machine';

const schema = yup.object().shape({
  machine: yup.object().shape({
    name: yup.string().required('*'),
  }),
});

interface MachineScanResult {
  manufacturer: string;
  model: string;
  serialNumber: string;
  softwareVersion: string;
  urCapVersion: string;
  threePositionEnabled: boolean;
}

enum RegistrationStatus {
  IDLE = 'IDLE',
  REGISTERING = 'REGISTERING',
  REGISTERED = 'REGISTERED',
  ERROR = 'ERROR',
}

interface RegisterMachineDirectConnectQRCode extends RegisterMachineProps {
  onRegistration: (provisionedMachine: ProvisionedMachine) => Promise<void>;
}

const RegisterMachineDirectConnectQRCode = (
  props: RegisterMachineDirectConnectQRCode
): ReactElement => {
  const { machine, teamId, onRegistration } = props;
  const {
    state: _state,
    __typename,
    firmwareVersion: _firmwareVersion,
    configuration: _configuration,
    clientId: _clientId,
    ...reactivateMachineProps
  } = machine ?? {};

  const formikRef = useRef<FormikProps<ProvisionMachineInput> | null>(null);
  const [provisionMachine] = useProvisionMachineMutation();
  const [registrationStatus, setRegistrationStatus] = useState(
    RegistrationStatus.IDLE
  );
  const [qrCodeValid, setQrCodeValid] = useState(false);
  const [fetchScratchpadApps] = useListScratchpadAppsLazyQuery();

  const registerDirectMachine = useCallback(
    async (params: {
      provisionMachineInput: ProvisionMachineInput;
    }): Promise<ProvisionedMachine | undefined> => {
      logger.info('Registering direct machine', { params });
      const { provisionMachineInput } = params;
      setRegistrationStatus(RegistrationStatus.REGISTERING);

      // provision the machine
      let provisionResult: ProvisionedMachine | undefined;
      try {
        const response = await provisionMachine({
          variables: {
            input: provisionMachineInput,
          },
          refetchQueries: [ListMachinesDocument],
        });

        provisionResult = response.data?.provisionMachine;

        if (!provisionResult) {
          logger.error('Machine was not provisioned', {
            provisionMachineInput,
          });
          setRegistrationStatus(RegistrationStatus.ERROR);
          return;
        }

        logger.info('Successfully provisioned machine!');
      } catch (error) {
        logger.error('Failed to provision machine', {
          error,
          provisionMachineInput,
        });
        setRegistrationStatus(RegistrationStatus.ERROR);
        return;
      }

      const machineId = provisionResult.machine.id;

      // fetch any scratchpad apps
      try {
        const { error: fetchScratchpadAppsError } = await fetchScratchpadApps({
          variables: { input: { machineId } },
        });

        if (fetchScratchpadAppsError) {
          logger.error('Failed to fetch scratchpad apps', {
            fetchScratchpadAppsError,
          });
          setRegistrationStatus(RegistrationStatus.ERROR);
          return;
        }

        logger.info('Successfully fetched any scratchpad apps for machine');
      } catch (error) {
        logger.error('Failed to fetch scratchpad apps', {
          error,
          machineId,
          provisionMachineInput,
        });
        setRegistrationStatus(RegistrationStatus.ERROR);
        return;
      }

      if (provisionMachineInput.machine.id) {
        trackReactivatingMachine(provisionResult.machine);
      } else {
        trackRegisterMachine(provisionResult.machine);
      }

      setRegistrationStatus(RegistrationStatus.REGISTERED);
      return provisionResult;
    },
    [fetchScratchpadApps, provisionMachine]
  );

  const checkValidQrCode = useCallback((parsedData: any) => {
    return (
      !isEmpty(parsedData.serialNumber) &&
      !isEmpty(parsedData.manufacturer) &&
      !isEmpty(parsedData.model)
    );
  }, []);

  const buildProvisionInput = useCallback(
    (params: {
      scannedData: string;
      formData: ProvisionMachineInput;
    }): ProvisionMachineInput | undefined => {
      const { scannedData, formData } = params;
      try {
        const scanJson: MachineScanResult = JSON.parse(scannedData);
        const isQrCodeValid = checkValidQrCode(scanJson);
        setQrCodeValid(isQrCodeValid);
        /**
          {
            "manufacturer": "Universal Robots",
            "model": "UR10",
            "serialNumber": "20215200479",
            "softwareVersion": "5.13.1",
            "urCapVersion": "1.3.3",
            "threePositionEnabled": false,
          }
        **/
        const {
          manufacturer,
          model,
          serialNumber,
          softwareVersion,
          threePositionEnabled,
        } = scanJson;

        const registrationId = uuid();

        const merged: ProvisionMachineInput = merge({}, formData, {
          context: {
            registrationId,
            manufacturer,
            model,
            serialNumber,
            softwareVersion,
            threePositionEnabled,
            provisionOffline: true,
          },
          machine: {
            // Server pulls software version from the context variable above,
            // but too scared to remove it right now.
            softwareVersion,
            demo: false,
            connectionType: MachineConnectionType.DIRECT,
          },
        } as ProvisionMachineInput);

        return merged;
      } catch (error) {
        logger.warn('Error parsing scan data', { error, scannedData });
      }
    },
    [checkValidQrCode]
  );

  const handleScanData = useCallback(
    async (scannedData: string) => {
      if (isNil(formikRef.current)) {
        logger.error('Preventing scan data due to missing formikRef');
        return;
      }

      const provisionMachineInput = buildProvisionInput({
        scannedData,
        formData: formikRef.current.values,
      });

      if (!provisionMachineInput) {
        logger.error('Failed to build provisionMachineInput', {
          scannedData,
          formikValues: formikRef.current.values,
        });
        return;
      }

      const registeredMachine = await registerDirectMachine({
        provisionMachineInput,
      });

      if (!registeredMachine) {
        logger.error('Failed to register machine', { provisionMachineInput });
        setRegistrationStatus(RegistrationStatus.ERROR);
        return;
      }

      await onRegistration(registeredMachine);
    },
    [buildProvisionInput, onRegistration, registerDirectMachine]
  );

  const initialValues: ProvisionMachineInput = useMemo(
    () => ({
      context: {},
      machine: {
        teamId,
        ...reactivateMachineProps,
        active: true,
      },
    }),
    [reactivateMachineProps, teamId]
  );

  return (
    <>
      <Formik<ProvisionMachineInput>
        initialValues={initialValues}
        validationSchema={schema}
        enableReinitialize={true}
        onSubmit={() => {
          /* no-op */
        }}
        validateOnMount={true}
        innerRef={(instance) => (formikRef.current = instance)}
      >
        {({ values }) => {
          const hasMachineName = !isEmpty(values.machine.name);

          return (
            <Form>
              <IonList lines="none">
                <IonFormField
                  label={i18n.t('common:machine_name')}
                  name="machine.name"
                  inputProps={{
                    type: 'text',
                    autocapitalize: 'on',
                  }}
                />
              </IonList>
              <QrCodeScannerButton
                handleScanData={handleScanData}
                isValid={qrCodeValid}
                isDisabled={!hasMachineName}
              />
              {isLocalDev() && (
                <IonButton
                  className="ion-margin"
                  type="button"
                  fill="outline"
                  expand="block"
                  disabled={!hasMachineName}
                  onClick={() => {
                    const mockScanResult: MachineScanResult = {
                      ...ApplicationConfig.simulate.barcode,
                      threePositionEnabled: false,
                    };
                    handleScanData(JSON.stringify(mockScanResult));
                  }}
                >
                  {i18n.t('common:manual')}
                </IonButton>
              )}
            </Form>
          );
        }}
      </Formik>
      <SaveStatusToast
        loading={registrationStatus === RegistrationStatus.REGISTERING}
        error={registrationStatus === RegistrationStatus.ERROR}
        showOnly="error"
      />
    </>
  );
};

export { RegisterMachineDirectConnectQRCode };
